import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  Trash2,
} from "lucide-react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  DialogTitle,
} from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import type FileAssetDTO from "../../types/dtos/fileAssets/FileAssetDTO";
import type FileProps from "../../types/ui/files/FileProps";
import { useDownloadFileAsset } from "../../hooks/queries";
import FilePreview from "./FilePreview";
import Button from "../ui/button/Button";
import { Image } from "../ui/image/Image";
import { useDialog } from "../../hooks/contexts";
import { FileType } from "../../types/dtos/fileAssets/FileAssetType";

export interface FileModalPreviewProps extends FileProps {
  files: FileAssetDTO[];
  onClose: () => void;
}

const ImageTypeIcon = (fileType: FileType) => {
  let iconSrc: string = "";

  switch (fileType) {
    case FileType.ImageJpeg:
    case FileType.ImagePng:
      iconSrc = "images/file-icons/image.png";
      break;
    case FileType.Pdf:
      iconSrc = "images/file-icons/pdf.png";
      break;
    case FileType.WordProcessingDoc:
    case FileType.MsWord:
      iconSrc = "images/file-icons/word.png";
      break;
    default:
      iconSrc = "images/file-icons/file.png";
  }

  return (
    <Image src={iconSrc} alt="File Icon" className="w-10 h-10 object-contain" />
  );
};

export default function FileModalPreview({
  files,
  index,
  onClose,
  onDelete,
}: FileModalPreviewProps) {
  const dialog = useDialog();
  const { mutateAsync: downloadAsset } = useDownloadFileAsset();

  const [current, setCurrent] = useState(index);
  const [zoom, setZoom] = useState(1);

  // reset zoom on file change
  useEffect(() => {
    setZoom(1);
  }, [current]);

  if (!files || files.length === 0) return <Skeleton />;

  const file = files[current];

  const next = () => {
    if (current < files.length - 1) {
      setCurrent((prev) => prev + 1);
    }
  };

  const prev = () => {
    if (current > 0) {
      setCurrent((prev) => prev - 1);
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div
        className={
          "fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3"
        }
      >
        <DialogBackdrop className="fixed inset-0 bg-black/60" />

        <DialogPanel className="fixed inset-8 flex items-center justify-center rounded-2xl">
          <div className="grid grid-rows-[auto_1fr] w-full h-full rounded-2xl shadow-lg overflow-hidden bg-white border-gray-50">
            <DialogTitle className="flex items-center justify-between px-4 py-4 bg-white text-2xl font-medium  w-full">
              <div className="flex items-center gap-2">
                <div>
                  {file ? ImageTypeIcon(file.ContentType) : <Skeleton />}
                </div>

                <div className="text-base">
                  {file ? file.OriginalFileName : <Skeleton />}
                </div>
              </div>

              <X
                className="h-6 w-6 text-gray-500 hover:cursor-pointer"
                onClick={onClose}
              />
            </DialogTitle>

            <div className="flex flex-1 relative bg-gray-100">
              {/* LEFT ARROW */}
              <Button
                size="sm"
                variant="ghost"
                onClick={prev}
                disabled={current === 0}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow hover:bg-gray-200 disabled:opacity-30 h-12.5 w-12.5"
              >
                <ChevronLeft />
              </Button>

              {/* PREVIEW */}
              <div className="flex-1 flex items-center justify-center overflow-auto">
                <div className="flex items-center justify-center">
                  <div
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: "center",
                    }}
                    className="max-w-[80vw] max-h-[80vh]"
                  >
                    <FilePreview {...file} FileSize="large" />
                  </div>
                </div>
              </div>

              {/* RIGHT ARROW */}
              <Button
                size="sm"
                variant="ghost"
                onClick={next}
                disabled={current === files.length - 1}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow hover:bg-gray-200 disabled:opacity-30 h-12.5 w-12.5"
              >
                <ChevronRight />
              </Button>
            </div>

            {/* 🔥 TOOLBAR */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-xl px-4 py-2 flex items-center gap-4">
              {/* ZOOM IN */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setZoom((z) => Math.min(z + 0.2, 3))}
                className="cursor-pointer text-gray-600 hover:text-black"
              >
                <ZoomIn size={18} />
              </Button>

              {/* ZOOM OUT */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
                className="cursor-pointer text-gray-600 hover:text-black"
              >
                <ZoomOut size={18} />
              </Button>

              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await downloadAsset(file.Id);
                  return;
                }}
                className="cursor-pointer text-gray-600 hover:text-black"
              >
                <Download size={18} />
              </Button>

              {/* DELETE */}
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  if (
                    await dialog.confirm({
                      title: "Confirm Deletion",
                      message: `Are you sure you want to delete "${file.OriginalFileName}"? This action cannot be undone.`,
                      confirmText: "Delete",
                    })
                  ) {
                    onDelete?.(file.Id);
                  }
                }}
                className="cursor-pointer text-red-500 hover:text-red-700"
              >
                <Trash2 size={18} />
              </Button>

              {/* ZOOM % */}
              <span className="text-xs text-gray-500">
                {Math.round(zoom * 100)}%
              </span>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
